import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useOpenOnNew from '../hooks/useOpenOnNew';
import { useAuth } from '../context/AuthContext';
import { getTasks } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import {
  keys, errorMessage,
  useTaskBoard, useAccountsLookup, useContactsLookup, useOpportunitiesLookup, useUsersLookup,
  useCreateTask, useCompleteTask, useReopenTask, useDeleteTask, usePatchTask, useBulkUpdateTasks,
  useCreateAccount, useCreateContact,
} from '../api/queries';
import { useToast } from '../context/ToastContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableHeader from '../components/SortableHeader';
import { ColumnsButton } from '../components/ColumnConfigurator';
import SavedViews from '../components/SavedViews';
import MultiSelectFilter from '../components/MultiSelectFilter';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import { Plus, Search, CheckSquare, Check, RotateCcw, Trash2, AlertCircle, Target, ChevronDown, X, List, LayoutGrid, Pencil } from 'lucide-react';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import TypeaheadSelect from '../components/TypeaheadSelect';
import ActionItemsKanban, { GROUP_BY_OPTIONS } from '../components/ActionItemsKanban';
import ActionItemEditModal from '../components/ActionItemEditModal';

function ActionItems() {
  const { user, canDo } = useAuth();
  const { visibleColumns } = useColumns('tasks');
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const queryClient = useQueryClient();
  const toast = useToast();

  // Reference lists, from the cache. A hidden module gives an empty list.
  const { data: users = [] } = useUsersLookup();
  const { data: accounts = [] } = useAccountsLookup();
  const { data: contacts = [] } = useContactsLookup();
  const { data: opportunities = [] } = useOpportunitiesLookup();
  const [lookupError, setLookupError] = useState('');

  // Writes. Each one refreshes every cached view of action items when it lands —
  // this list, the board, the dashboard, the account page.
  const createMutation = useCreateTask();
  const completeMutation = useCompleteTask();
  const reopenMutation = useReopenTask();
  const deleteMutation = useDeleteTask();
  const patchMutation = usePatchTask();
  const bulkMutation = useBulkUpdateTasks();
  const createAccountMutation = useCreateAccount();
  const createContactMutation = useCreateContact();

  // View mode
  const [viewMode, setViewMode] = useState('table');
  const [groupBy, setGroupBy] = useState('due_date');

  // Filters - initialize from URL params
  const [statusFilter, setStatusFilter] = useState(() => {
    const param = searchParams.get('status');
    return param ? param.split(',') : ['Open'];
  });
  const [assignedFilter, setAssignedFilter] = useState([]);
  const [dueFilter, setDueFilter] = useState(searchParams.get('overdue') === 'true' ? 'overdue' : '');

  // Build extra params for the pagination hook
  const extraParams = useMemo(() => {
    const p = {};
    if (statusFilter.length) p.status = statusFilter.join(',');
    if (assignedFilter.length) p.assigned_to = assignedFilter.join(',');
    if (dueFilter === 'overdue') {
      p.overdue = 'true';
    } else if (dueFilter === 'today') {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      p.due_after = dateStr;
      p.due_before = dateStr + 'T23:59:59';
    } else if (dueFilter === 'this_week') {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (7 - dayOfWeek));
      p.due_after = today.toISOString().split('T')[0];
      p.due_before = endOfWeek.toISOString().split('T')[0] + 'T23:59:59';
    } else if (dueFilter === 'this_month') {
      const today = new Date();
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      p.due_after = today.toISOString().split('T')[0];
      p.due_before = endOfMonth.toISOString().split('T')[0] + 'T23:59:59';
    }
    return p;
  }, [statusFilter, assignedFilter, dueFilter]);

  // Server-side pagination via hook (table view)
  const {
    data: actionItems,
    loading,
    error: paginationError,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sortBy: sortField,
    sortOrder: sortDirection,
    handleSort,
    total,
    totalPages,
  } = usePagination(getTasks, {
    queryKey: 'tasks',
    initialLimit: 25,
    initialSortBy: 'due_date',
    initialSortOrder: 'asc',
    debounceMs: 300,
    extraParams,
  });

  const error = lookupError || paginationError;

  // --- Kanban view gets the full unpaginated result set ---
  // Debounced so typing in the search box doesn't fire a request per keystroke.
  const [boardSearch, setBoardSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setBoardSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const boardParams = useMemo(() => ({ search: boardSearch || undefined, ...extraParams }), [boardSearch, extraParams]);
  const boardQuery = useTaskBoard(boardParams, viewMode === 'kanban');
  const kanbanData = boardQuery.data || [];
  const kanbanLoading = viewMode === 'kanban' && boardQuery.isPending;

  // Modals & selection
  const [showCreateModal, setShowCreateModal] = useState(false);
  useOpenOnNew(() => setShowCreateModal(true));
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [formData, setFormData] = useState({ title: '', due_date: '', account_id: '', contact_id: '', opportunity_id: '', assigned_to: '' });
  const [accountSearchText, setAccountSearchText] = useState('');
  const [contactSearchText, setContactSearchText] = useState('');
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');


  const resetFormState = () => {
    setFormData({ title: '', due_date: '', account_id: '', contact_id: '', opportunity_id: '', assigned_to: '' });
    setAccountSearchText('');
    setContactSearchText('');
    setShowNewAccountForm(false);
    setShowNewContactForm(false);
    setNewAccountName('');
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({ ...formData, assigned_to: formData.assigned_to || user.id });
      setShowCreateModal(false);
      resetFormState();
      toast.success('Action item created');
    } catch (err) { setLookupError(errorMessage(err, 'Failed to create action item')); }
  };

  const handleCreateNewAccount = async () => {
    if (!newAccountName.trim()) return;
    try {
      const res = await createAccountMutation.mutateAsync({ name: newAccountName.trim() });
      const newAccount = res.data;
      setFormData(prev => ({ ...prev, account_id: newAccount.id, contact_id: '', opportunity_id: '' }));
      setAccountSearchText(newAccountName.trim());
      setShowNewAccountForm(false);
      setNewAccountName('');
    } catch (err) { setLookupError(errorMessage(err, 'Failed to create account')); }
  };

  const handleCreateNewContact = async () => {
    if (!newContactFirstName.trim()) return;
    try {
      const fullName = [newContactFirstName.trim(), newContactLastName.trim()].filter(Boolean).join(' ');
      const res = await createContactMutation.mutateAsync({
        first_name: newContactFirstName.trim(),
        last_name: newContactLastName.trim(),
        name: fullName,
        email: newContactEmail.trim() || undefined,
        account_id: formData.account_id,
      });
      const newContact = res.data;
      setFormData(prev => ({ ...prev, contact_id: newContact.id }));
      setContactSearchText(fullName);
      setShowNewContactForm(false);
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
    } catch (err) { setLookupError(errorMessage(err, 'Failed to create contact')); }
  };

  const filteredContacts = contacts.filter(c =>
    !formData.account_id || c.account_id === parseInt(formData.account_id)
  );

  // Complete and reopen are each other's undo, so neither asks first.
  const handleComplete = async (item) => {
    try {
      await completeMutation.mutateAsync(item.id);
      toast.undo(`“${item.title}” marked complete`, () => reopenMutation.mutate(item.id));
    } catch (err) { setLookupError(errorMessage(err, 'Failed to complete action item')); }
  };

  const handleReopen = async (item) => {
    try {
      await reopenMutation.mutateAsync(item.id);
      toast.undo(`“${item.title}” reopened`, () => completeMutation.mutate(item.id));
    } catch (err) { setLookupError(errorMessage(err, 'Failed to reopen action item')); }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteConfirm);
      setDeleteConfirm(null);
      toast.success('Action item deleted');
    } catch (err) { setLookupError(errorMessage(err, 'Failed to delete action item')); }
  };

  // Applied when a card is dragged between Kanban columns. Optimistic so the
  // card lands immediately; rolled back if the server rejects the change.
  const handleKanbanChange = async (id, patch) => {
    const boardKey = keys.board('tasks', boardParams);
    const previous = queryClient.getQueryData(boardKey);
    queryClient.setQueryData(boardKey, (items) => (items || []).map(i => (i.id === id ? { ...i, ...patch } : i)));
    try {
      await patchMutation.mutateAsync({ id, patch });
      setLookupError('');
    } catch (err) {
      queryClient.setQueryData(boardKey, previous);
      setLookupError(errorMessage(err, 'Failed to update action item'));
    }
  };

  const isOverdue = (item) => item.status === 'Open' && item.due_date && new Date(item.due_date) < new Date();

  const toggleSelectAll = () => {
    if (selectedIds.length === actionItems.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(actionItems.map(t => t.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkUpdate = async (updates) => {
    try {
      const count = selectedIds.length;
      await bulkMutation.mutateAsync({ ids: selectedIds, updates });
      setSelectedIds([]);
      setShowBulkActions(false);
      toast.success(`${count} action item${count === 1 ? '' : 's'} updated`);
    } catch (err) {
      setLookupError(errorMessage(err, 'Failed to update action items'));
    }
  };

  const clearFilters = () => { setSearch(''); setStatusFilter([]); setAssignedFilter([]); setDueFilter(''); };
  const hasFilters = !!(search || statusFilter.length > 0 || assignedFilter.length > 0 || dueFilter);

  const getRowActions = (item) => [
    canDo('tasks', 'edit') && {
      key: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: () => setEditing(item),
    },
    canDo('tasks', 'edit') && item.status === 'Open' && {
      key: 'complete',
      label: 'Mark Complete',
      icon: Check,
      onClick: () => handleComplete(item),
    },
    canDo('tasks', 'edit') && item.status === 'Completed' && {
      key: 'reopen',
      label: 'Reopen',
      icon: RotateCcw,
      onClick: () => handleReopen(item),
    },
    canDo('tasks', 'delete') && { divider: true },
    canDo('tasks', 'delete') && {
      key: 'delete',
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => setDeleteConfirm(item.id),
    },
  ];

  const renderCell = (col, item) => {
    switch (col.key) {
      case 'title':
        return <span className={item.status === 'Completed' ? 'text-muted line-through' : ''}>{item.title}</span>;
      case 'account_name':
        return item.account_name ? <Link to={`/accounts/${item.account_id}`}>{item.account_name}</Link> : <EmptyValue />;
      case 'opportunity_name':
        return item.opportunity_name ? <Link to={`/opportunities/${item.opportunity_id}`}><Target size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{item.opportunity_name}</Link> : <EmptyValue />;
      case 'due_date':
        return item.due_date ? (
          <span className={isOverdue(item) ? 'text-danger flex items-center gap-1' : ''}>
            {isOverdue(item) && <AlertCircle size={14} />}
            {new Date(item.due_date).toLocaleDateString()}
          </span>
        ) : <EmptyValue />;
      case 'assigned_to_name':
        return item.assigned_to_name || <EmptyValue />;
      case 'status':
        return <span className={`badge ${item.status === 'Completed' ? 'badge-success' : 'badge-warning'}`}>{item.status}</span>;
      case 'created_at':
        return item.created_at ? new Date(item.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return item[col.key] || <EmptyValue />;
    }
  };

  return (
    <div className={`action-items-page${viewMode === 'kanban' ? ' kanban-active' : ''}`}>
      <div className="page-header">
        <h1 className="page-title">Action Items</h1>
        <div className="page-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <List size={16} />
              Table
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={16} />
              Kanban
            </button>
          </div>
          <SavedViews entityType="tasks" current={{ search, statusFilter, assignedFilter, dueFilter, viewMode, groupBy, sortBy: sortField, sortOrder: sortDirection }} onApply={(v) => { setSearch(v.search || ''); setStatusFilter(v.statusFilter || []); setAssignedFilter(v.assignedFilter || []); setDueFilter(v.dueFilter || ''); if (v.viewMode) setViewMode(v.viewMode); if (v.groupBy) setGroupBy(v.groupBy); if (v.sortBy) handleSort(v.sortBy, v.sortOrder || 'asc'); }} />
          {viewMode === 'kanban' ? (
            <div className="kanban-group-by">
              <span className="kanban-group-by-label">Group by</span>
              <select
                className="form-select filter-dropdown-sm"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                aria-label="Group Kanban board by"
              >
                {GROUP_BY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <ColumnsButton entityType="tasks" />
          )}
          {canDo('tasks', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}><Plus size={18} /> New Action Item</button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input type="text" className="form-input" placeholder="Search action items..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search action items" />
            </div>
            <MultiSelectFilter
              label="Status"
              options={[{ id: 'Open', name: 'Open' }, { id: 'Completed', name: 'Completed' }]}
              selected={statusFilter}
              onChange={setStatusFilter}
              className="filter-dropdown-sm"
              ariaLabel="Filter by status"
            />
            <MultiSelectFilter
              label="Users"
              options={users}
              selected={assignedFilter}
              onChange={setAssignedFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by assigned user"
            />
            <select className="form-select filter-dropdown-md" value={dueFilter} onChange={(e) => setDueFilter(e.target.value)} aria-label="Filter by due date">
              <option value="">All Due Dates</option>
              <option value="overdue">Overdue</option>
              <option value="today">Due Today</option>
              <option value="this_week">Due This Week</option>
              <option value="this_month">Due This Month</option>
            </select>
            {hasFilters && (
              <button className="btn-clear-filters" onClick={clearFilters}>
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions (table view only) */}
      {viewMode === 'table' && selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {canDo('tasks', 'edit') && (
              <div className="dropdown">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowBulkActions(showBulkActions === 'assignee' ? false : 'assignee')}
                >
                  Reassign To
                  <ChevronDown size={16} />
                </button>
                {showBulkActions === 'assignee' && (
                  <div className="dropdown-menu">
                    {users.map(u => (
                      <button
                        key={u.id}
                        className="dropdown-item"
                        onClick={() => handleBulkUpdate({ assigned_to: u.id })}
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        kanbanLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <ActionItemsKanban
            actionItems={kanbanData}
            users={users}
            groupBy={groupBy}
            onItemChange={handleKanbanChange}
            canEdit={canDo('tasks', 'edit')}
          />
        )
      ) : isMobile ? (
        /* Mobile Card Layout */
        <div className="card">
          {loading ? (
            <CardSkeleton count={4} />
          ) : actionItems.length === 0 ? (
            <div className="card-body">
              {hasFilters ? (
                <EmptyStateFiltered onClearFilters={clearFilters} />
              ) : (
                <EmptyState
                  icon={CheckSquare}
                  title="No action items yet"
                  description="Create your first action item to stay on top of your to-dos and follow-ups."
                  actionLabel={canDo('tasks', 'create') ? 'New Action Item' : undefined}
                  onAction={canDo('tasks', 'create') ? () => setShowCreateModal(true) : undefined}
                  actionIcon={Plus}
                />
              )}
            </div>
          ) : (
            <>
              {actionItems.map(item => (
                <MobileCard
                  key={item.id}
                  columns={visibleColumns}
                  data={item}
                  renderCell={renderCell}
                  selected={selectedIds.includes(item.id)}
                  onSelect={() => toggleSelect(item.id)}
                  primaryField="title"
                  actions={<RowActions actions={getRowActions(item)} id={item.id} />}
                />
              ))}
            </>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      ) : (
        /* Desktop Table Layout */
        <div className="card">
          <div className="table-container">
            <table className="table" aria-label="Action items list">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.length === actionItems.length && actionItems.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all action items"
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={true} hasActions={true} />
                ) : actionItems.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2}>
                      {hasFilters ? (
                        <EmptyStateFiltered onClearFilters={clearFilters} />
                      ) : (
                        <EmptyState
                          icon={CheckSquare}
                          title="No action items yet"
                          description="Create your first action item to stay on top of your to-dos and follow-ups."
                          actionLabel={canDo('tasks', 'create') ? 'New Action Item' : undefined}
                          onAction={canDo('tasks', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  actionItems.map(item => (
                    <tr key={item.id} className={isOverdue(item) ? 'overdue-row' : ''}>
                      <td>
                        <input type="checkbox" className="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} />
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderCell(col, item)}</td>
                      ))}
                      <td>
                        <RowActions actions={getRowActions(item)} id={item.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}

      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetFormState(); }} title="New Action Item">
        <form onSubmit={handleCreate}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="action-item-title">Action Item *</label>
              <input id="action-item-title" type="text" className="form-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required aria-required="true" />
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label" htmlFor="action-item-due-date">Due Date</label>
                <input id="action-item-due-date" type="datetime-local" className="form-input" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="action-item-assigned-to">Assign To</label>
                <select id="action-item-assigned-to" className="form-select" value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}>
                  <option value="">Assign to me</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="action-item-account">Related Account</label>
              {showNewAccountForm ? (
                <div className="inline-create-form">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Account name *"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    autoFocus
                  />
                  <div className="inline-create-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewAccount} disabled={!newAccountName.trim()}>Create</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewAccountForm(false); setNewAccountName(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <TypeaheadSelect
                  id="action-item-account"
                  placeholder="Type to search accounts..."
                  items={accounts}
                  value={formData.account_id}
                  displayValue={accountSearchText}
                  onChange={(id, name) => {
                    setFormData({ ...formData, account_id: id, contact_id: '', opportunity_id: '' });
                    setAccountSearchText(name);
                    setContactSearchText('');
                  }}
                  onAddNew={() => setShowNewAccountForm(true)}
                  addNewLabel="Add New Account"
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="action-item-contact">Related Contact</label>
              {showNewContactForm ? (
                <div className="inline-create-form">
                  <div className="grid grid-cols-2">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="First name *"
                      value={newContactFirstName}
                      onChange={(e) => setNewContactFirstName(e.target.value)}
                      autoFocus
                    />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Last name"
                      value={newContactLastName}
                      onChange={(e) => setNewContactLastName(e.target.value)}
                    />
                  </div>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Email (optional)"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    style={{ marginTop: '8px' }}
                  />
                  <div className="inline-create-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewContact} disabled={!newContactFirstName.trim()}>Create</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewContactForm(false); setNewContactFirstName(''); setNewContactLastName(''); setNewContactEmail(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <TypeaheadSelect
                  id="action-item-contact"
                  placeholder={formData.account_id ? "Type to search contacts..." : "Select an account first"}
                  items={filteredContacts}
                  value={formData.contact_id}
                  displayValue={contactSearchText}
                  onChange={(id, name) => {
                    setFormData({ ...formData, contact_id: id });
                    setContactSearchText(name);
                  }}
                  onAddNew={formData.account_id ? () => setShowNewContactForm(true) : undefined}
                  addNewLabel="Add New Contact"
                  disabled={!formData.account_id}
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="action-item-opportunity">Related Opportunity</label>
              <select id="action-item-opportunity" className="form-select" value={formData.opportunity_id} onChange={(e) => setFormData({ ...formData, opportunity_id: e.target.value })} disabled={!formData.account_id}>
                <option value="">None</option>
                {opportunities
                  .filter(o => !formData.account_id || o.account_id === parseInt(formData.account_id))
                  .map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetFormState(); }}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Action Item</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDelete} title="Delete Action Item" message="Delete this action item?" confirmText="Delete" />
      <ActionItemEditModal task={editing} isOpen={!!editing} onClose={() => setEditing(null)} />
    </div>
  );
}

export default ActionItems;
